/* eslint-disable camelcase */
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import ColoredMaterialIcon from 'components/twilio/components/styled/ColoredMaterialIcon';
import { RecentActorsRounded } from '@material-ui/icons';
import { selectMomentLocalizer } from 'models/localization';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { nameByLocale } from 'hooks/useNameByLocale';
import IconButton from 'components/twilio/components/styled/ChatIconButton';
import UserAvatar from 'components/twilio/components/styled/UserAvatar';
import { selectParticipantsById } from 'models/participants';
import { setEnrichmentDataShow, setEnrichmentData } from 'models/enrichmentData';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import ChatIcon from './assets/ChatIcon';
import UserDetails from './UserDetails';

interface UserActivityLogItemProps {
  log: {
    action_at: any;
    booth_id: string;
    booth_name: string;
    event_action: string;
    event_domain: string;
    event_info: string;
    event_type: string;
    user_email: string;
    user_first_name: string;
    user_last_name: string;
    user_phone: string;
    user_token: string;
    user_uuid: string;
  }
  setActivityLogTarget: ({ type, value }: { type: string, value: string, attr?: any }) => void;
  locale: string;
  setFilter: (filter) => void;
  activityLogTarget: any;
  filter: any;
  chatEnabled: boolean;
}

const BASE_HEIGHT = 36;

const UserActivityLogItemContainer = styled.div`
  width: calc(100% - 32px);
  display: flex;
  margin: 0 16px 10px 16px;
  border: 2px solid #EFEFEF;
  border-radius: 4px;
  padding: 16px;
`;

const UserInfoContainer = styled.div`
  flex: 0 0 48px;
  height: ${BASE_HEIGHT}px;

  display: flex;
  align-items: center;
`;

const UserNameContainer = styled.div`
  height: ${BASE_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const UserNameDetailContainer = styled.div`
  padding-right: 16px;
  width: 230px;
`;

const UserName = styled.div<{ $disabled: boolean }>`
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;

  color: ${({ $disabled }) => ($disabled ? 'inherit' : '#1068BF')};
  cursor: ${({ $disabled }) => ($disabled ? 'inherit' : 'pointer')};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: ${({ $disabled }) => ($disabled ? 'inherit' : 'underline')};
  }
`;

const UserDescription = styled.div`
  font-size: 12px;
  line-height: 12px;
  height: 12px;
  margin-top: 2px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserActivity = styled.div`
  flex: 1 0;

  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

const ActionButton = styled.div`
  display: flex;
  flex: 0 0 76px;
  justify-content: space-between;
  height: ${BASE_HEIGHT}px;
`;

const EventMessageWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${BASE_HEIGHT}px;
`;

const EventMessage = styled.div<{ $disabled: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'inherit' : 'pointer')};
  color: ${({ $disabled }) => ($disabled ? 'inherit' : '#1068BF')};

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  word-wrap: break-word;

  @supports (-webkit-line-clamp: 2) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const EventInfoMessage = styled.span<{ $disabled: boolean }>`
  margin-top: 18px;
  padding-left: 4px;
  border-left: 2px solid #C4C4C4;
  font-size: 16px;

  color: ${({ $disabled }) => ($disabled ? 'inherit' : '#1068BF')};
  cursor: ${({ $disabled }) => ($disabled ? 'inherit' : 'pointer')};

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  word-wrap: break-word;

  @supports (-webkit-line-clamp: 2) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &:hover {
    text-decoration: ${({ $disabled }) => ($disabled ? 'inherit' : 'underline')};
  }
`;

const DateFromNowContainer = styled.div`
  font-family: Lato;
  font-size: 14px;
  line-height: 14px;

  flex: 0 0 80px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  height: ${BASE_HEIGHT}px;
  margin-right: 24px;
`;

const UserActivityLogItem: FC<UserActivityLogItemProps> = ({
  log: {
    user_first_name: userFirstName,
    user_last_name: userLastName,
    event_type: eventType,
    action_at: actionAt,
    event_info: eventInfo,
    user_token: userToken,
    user_uuid: userUuid,
  },
  setActivityLogTarget,
  locale,
  setFilter,
  activityLogTarget,
  filter,
  chatEnabled,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const momentLocalizer = useSelector(selectMomentLocalizer);
  const participant = useSelector((state) => selectParticipantsById(state, userUuid));
  const dateObject = useMemo(() => momentLocalizer(actionAt.toDate() as Date), [actionAt, momentLocalizer]);
  const localizedName = useMemo(() => nameByLocale(userFirstName, userLastName, locale), [userFirstName, userLastName, locale]);
  const [timeFromNow, setTimeFromNow] = useState<string>(dateObject.fromNow());
  const { createPrivateChat } = useTwilioEventFunctions();

  const eventMessage = useMemo(() => {
    switch (eventType) {
      case 'Enter':
        return (
          <EventMessage
            onClick={() => {
              if (!filter) {
                setFilter({ label: t('activity_log.enter_booth_message', 'Entered the booth'), value: 'Enter' });
              }
            }}
            $disabled={filter}
          >
            {t('activity_log.enter_booth_message', 'Entered the booth')}
          </EventMessage>
        );
      case 'File_View':
        return (
          <EventMessage
            onClick={() => {
              if (!activityLogTarget) {
                setActivityLogTarget({ type: 'event_info', value: eventInfo });
              }
            }}
            $disabled={activityLogTarget}
          >
            {t('activity_log.file_view_message', 'Viewed file(s)')}
          </EventMessage>
        );
      case 'Meetup':
        return (
          <EventMessage
            onClick={() => {
              if (!filter) {
                setFilter({ label: t('activity_log.meetup_attempt', 'Tried to schedule Meetup'), value: 'Meetup' });
              }
            }}
            $disabled={filter}
          >
            {t('activity_log.meetup_attempt', 'Tried to schedule Meetup')}
          </EventMessage>
        );
      case 'Submitted_Meetup':
        return (
          <EventMessage
            onClick={() => {
              if (!filter) {
                setFilter({ label: t('activity_log.scheduled_meetup', 'Scheduled Meetup'), value: 'Submitted_Meetup' });
              }
            }}
            $disabled={filter}
          >
            {t('activity_log.scheduled_meetup', 'Scheduled Meetup')}
          </EventMessage>
        );
      default: return null;
    }
  }, [t, eventType, setFilter, setActivityLogTarget, eventInfo, filter, activityLogTarget]);

  const eventInfoMessage = useMemo(() => {
    switch (eventType) {
      case 'File_View':
        return eventInfo;
      default:
        return null;
    }
  }, [eventType, eventInfo]);

  const setActivityLogTargetByEventInfo = useCallback(() => {
    if (eventType === 'File_View') {
      setActivityLogTarget({ type: 'event_info', value: eventInfo });
    }
    if (eventType === 'Enter') {
      setFilter({ label: t('activity_log.enter_booth_message', 'Entered the booth'), value: 'Enter' });
    }
  }, [eventType, setFilter, t, setActivityLogTarget, eventInfo]);

  useEffect(() => {
    const updateDisplayTime = setInterval(() => {
      setTimeFromNow(dateObject.fromNow());
    }, [5000]);
    return () => {
      clearInterval(updateDisplayTime);
    };
  });

  const openPrivateChatroom = useCallback(async () => {
    window.trackingEventToFirebase(null, 'Chat', 'OpenPrivateChatWindow', 'Participant_List_Click');
    await createPrivateChat(userUuid);
  }, [createPrivateChat, userUuid]);

  const disableNameOnClick = useMemo(() => (
    activityLogTarget?.type === 'user_token'
  ), [activityLogTarget]);

  const disableFileOnClick = useMemo(() => (
    activityLogTarget?.type === 'event_info'
  ), [activityLogTarget]);

  const {
    customAttendeeFields: {
      vexpo_chat_user_description: fallbackDescription = '',
    } = {},
    vexpoChatUserDescription,
    enrichment,
  } = participant || {};

  const showEnrichmentData = useCallback(() => {
    batch(() => {
      dispatch(setEnrichmentDataShow(true));
      dispatch(setEnrichmentData({
        ...enrichment,
        attendee: {
          name: participant.name,
          uuid: participant.uuid,
          tag: participant.vexpoChatTag,
          company: participant.company,
          desc: vexpoChatUserDescription || fallbackDescription,
        },
      }));
    });
  }, [dispatch, enrichment, participant, vexpoChatUserDescription, fallbackDescription]);

  const onNameClick = useCallback(() => {
    if (!disableNameOnClick) {
      setActivityLogTarget({
        type: 'user_token',
        value: userToken,
        attr: { userFirstName, userLastName },
      });
    }
    setFilter(null);
  }, [disableNameOnClick, setActivityLogTarget, setFilter, userFirstName, userLastName, userToken]);

  const onActivityClick = useCallback(() => {
    if (!disableFileOnClick) {
      setFilter(null);
      setActivityLogTargetByEventInfo();
    }
  }, [disableFileOnClick, setActivityLogTargetByEventInfo, setFilter]);

  if (!eventMessage) return null;
  return (
    <UserActivityLogItemContainer>
      <UserInfoContainer>
        <UserAvatar
          name={userFirstName}
          src={enrichment?.person?.avatar}
          srcType="avatar"
        />
      </UserInfoContainer>
      <UserNameDetailContainer>
        <UserNameContainer>
          <UserName onClick={onNameClick} $disabled={disableNameOnClick}>
            {localizedName}
          </UserName>
          {(vexpoChatUserDescription || fallbackDescription) && (
            <UserDescription>
              {vexpoChatUserDescription || fallbackDescription}
            </UserDescription>
          )}
        </UserNameContainer>
        <UserDetails
          seniority={enrichment?.person?.employment?.seniority}
          employees={enrichment?.company?.metrics?.employees}
          estimatedAnnualRevenue={enrichment?.company?.metrics?.estimatedAnnualRevenue}
        />
      </UserNameDetailContainer>
      <UserActivity>
        <EventMessageWrapper>{eventMessage}</EventMessageWrapper>
        {eventInfoMessage && (
          <EventInfoMessage onClick={onActivityClick} $disabled={disableFileOnClick}>
            {eventInfoMessage}
          </EventInfoMessage>
        )}
      </UserActivity>
      <DateFromNowContainer>
        {timeFromNow}
      </DateFromNowContainer>
      <ActionButton>
        {chatEnabled && (
          <IconButton onClick={openPrivateChatroom}>
            <ChatIcon style={{ paddingTop: 2, paddingLeft: 2 }} />
          </IconButton>
        )}
        {enrichment && (
          <IconButton onClick={showEnrichmentData}>
            <ColoredMaterialIcon Icon={RecentActorsRounded} color="#484848" />
          </IconButton>
        )}
      </ActionButton>
    </UserActivityLogItemContainer>
  );
};

export default UserActivityLogItem;
